/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/account/api/fetch-graphql';
import { handleNetworkError } from '@/account/lib/network-error';
import { handleFetchError } from '@/account/lib/fetch-error';
import { GET_REGIONS } from './graphql/getRegions.graphql';
import { transformRegions } from '@/account/data/transforms';
import { RegionsFormResponse } from '@/account/types';
import { RegionTransform } from '@/account/data/models';

export const getRegions = async (
  countryCode: string
): Promise<RegionTransform[] | []> => {
  const sessionStorageKey = `_account_regions_${countryCode}`;

  const sessionStorageCache = sessionStorage.getItem(sessionStorageKey);

  if (sessionStorageCache) {
    return JSON.parse(sessionStorageCache);
  }

  return await fetchGraphQl(GET_REGIONS, {
    method: 'GET',
    cache: 'no-cache',
    variables: { countryCode },
  })
    .then((response: RegionsFormResponse) => {
      if (response.errors?.length) return handleFetchError(response.errors);

      const transformedData = transformRegions(response);

      sessionStorage.setItem(
        sessionStorageKey,
        JSON.stringify(transformedData)
      );

      return transformedData;
    })
    .catch(handleNetworkError);
};
